import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import moment from 'moment';

import { getBookedHolidaysCount, getPendingHistoryList } from 'reducers';
import { approveHolidayRequests, rejectHolidayRequest } from 'actions/employeeActions';

import HolidayRejectDialog from './HolidayRejectDialog';
import TableWrapper from 'components/Shared/TableWrapper';
import ConfirmDeleteDialog from 'components/Shared/ConfirmDeleteDialog';

import {
    TABLE_COLUMNS,
    DEFAULT_TABLE_COLUMNS,
    SORT_QUERY_SELECTOR,
    PAGE_QUERY_SELECTOR,
    DEFAULT_ITEMS_PER_PAGE,
    TABLE_INITIAL_QUERY,
    CONFIRM_DELETE_DIALOG_TITLE,
} from './PendingHolidaysTableConstants';
import { DATE_FORMAT_WITH_DOTS_FULL_YEAR } from 'constants/commonConstants';

import { parseQueryString } from 'utils/helpers';
import { convertStringToLanguageKey } from 'utils/languageUtils';

import { ReactComponent as AddIcon } from 'assets/add-button-icon.svg';
import { HOLIDAY_TYPES } from '../HolidaysRequests/HolidayRequestDialog/HolidayRequestDialogConstants';
import { HOLIDAY_TYPES_FULL_NAMES_MAP } from 'constants/holidayConstants';

const PendingHolidaysTable = ({
    agencyId,
    userId,
    departmentId,
    isAdminOrSupplierAdmin,
    accessAsManager,
    isTableDataLoading,
    pendingHolidaysCount,
    deleteHolidayRequests,
    handleHolidaysAction,
    approveHolidayRequests,
    rejectHolidayRequest,
    handleOpenDialog,
}) => {
    const intl = useIntl();

    const history = useHistory();
    const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
    const [targetHoliday, setTargetHoliday] = useState({});

    const [checkedRows, setCheckedRows] = useState([]);

    const dataSelector = (state, page) => {
        const parsedQuery = parseQueryString(history.location.search);
        const parsedQuerySortBy = parsedQuery[SORT_QUERY_SELECTOR] || TABLE_INITIAL_QUERY.sortBy;
        const sortBy = TABLE_COLUMNS.find(x => x.value === parsedQuerySortBy).propertyName;
        const sortByDir = parsedQuery[`${SORT_QUERY_SELECTOR}Dir`] || TABLE_INITIAL_QUERY.sortByDir;

        return getPendingHistoryList(state, page - 1, DEFAULT_ITEMS_PER_PAGE, sortBy, sortByDir);
    };

    // Approve
    const handleHolidayApprove = holiday => {
        handleHolidaysAction(approveHolidayRequests, [holiday]);
    };

    // Approve checked rows
    const handleSelectedHolidaysApprove = () => {
        handleHolidaysAction(approveHolidayRequests, checkedRows);
        setCheckedRows([]);
    };

    // Reject 
    const handleOpenRejectDialog = holiday => {
        setTargetHoliday(holiday);
        setIsRejectDialogOpen(true);
    };

    const handleCloseRejectDialog = () => {
        setTargetHoliday({});
        setIsRejectDialogOpen(false);
    };

    const handleHolidayRejectSubmit = rejectionReason => {
        handleHolidaysAction(rejectHolidayRequest, { ...targetHoliday, rejectionReason });
        handleCloseRejectDialog();
    };

    // Confirm Delete
    const handleOpenConfirmDeleteDialog = holiday => {
        setTargetHoliday(holiday);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleCloseConfirmDeleteDialog = () => {
        setTargetHoliday({});
        setIsConfirmDeleteDialogOpen(false);
    };

    const handleConfirmDeleteSubmit = () => {
        deleteHolidayRequests(userId, agencyId, departmentId, [targetHoliday._id], true, accessAsManager);
        handleCloseConfirmDeleteDialog();
    };

    const adjustPendingHolidaysKeys = pendingHolidays => pendingHolidays.map(pendingHoliday => {
        let holidayTypeFullName;

        if (pendingHoliday.type === HOLIDAY_TYPES.PAID && (pendingHoliday.byArticle === '155' || pendingHoliday.byArticle === '169')) {
            holidayTypeFullName = intl.formatMessage({ id: convertStringToLanguageKey(HOLIDAY_TYPES_FULL_NAMES_MAP[pendingHoliday.byArticle]) });
        }

        return {
            from: moment.utc(pendingHoliday.startDate).format(DATE_FORMAT_WITH_DOTS_FULL_YEAR),
            to: moment.utc(pendingHoliday.endDate).format(DATE_FORMAT_WITH_DOTS_FULL_YEAR),
            numberOfDays: pendingHoliday.numberOfDays,
            type: pendingHoliday.type === HOLIDAY_TYPES.PAID
                && pendingHoliday.byArticle ? `${intl.formatMessage({ id: convertStringToLanguageKey(pendingHoliday.type) })} ${intl.formatMessage({ id: "by-article-text" })} ${pendingHoliday.byArticle}${holidayTypeFullName ? ` - ${holidayTypeFullName}` : ''}${pendingHoliday.description ? `, ${intl.formatMessage({ id: convertStringToLanguageKey(pendingHoliday.description) })}` : ''}`
                : intl.formatMessage({ id: convertStringToLanguageKey(pendingHoliday.type) }),
            requestedOn: moment.utc(pendingHoliday.requestedOn).format(DATE_FORMAT_WITH_DOTS_FULL_YEAR),
            item: pendingHoliday,
        };
    });

    const actions = [
        { title: intl.formatMessage({ id: "approve" }), handleClick: handleHolidayApprove },
        { title: intl.formatMessage({ id: "reject" }), handleClick: handleOpenRejectDialog },
        { title: intl.formatMessage({ id: "delete" }), handleClick: handleOpenConfirmDeleteDialog }
    ];

    const getActionsHandler = () => actions;

    return (
        <div className="holidays-pending-table-wrapper">
            <TableWrapper
                title={intl.formatMessage({ id: "pending-holidays-section-title" })}
                columns={TABLE_COLUMNS}
                defaultColumns={DEFAULT_TABLE_COLUMNS}
                dataSelector={dataSelector}
                sortQuerySelector={SORT_QUERY_SELECTOR}
                adjustKeysFunction={adjustPendingHolidaysKeys}
                isTableDataLoading={isTableDataLoading}
                addMargin
                withPagination
                withCheckboxes={isAdminOrSupplierAdmin || accessAsManager?.length > 0}
                checkedRows={checkedRows}
                setCheckedRows={setCheckedRows}
                withActions={isAdminOrSupplierAdmin || accessAsManager?.length > 0}
                getActionsHandler={getActionsHandler}
                pagesCount={Math.ceil(pendingHolidaysCount / DEFAULT_ITEMS_PER_PAGE)}
                pageQuerySelector={PAGE_QUERY_SELECTOR}
            >
                <TableWrapper.TableAlignedItems>
                    <TableWrapper.TableAlignedItems.LeftAligned>
                        <TableWrapper.TableAlignedItems.TableHeader />
                    </TableWrapper.TableAlignedItems.LeftAligned>
                    <TableWrapper.TableAlignedItems.RightAligned>
                        {checkedRows.length > 0 && (isAdminOrSupplierAdmin || accessAsManager?.length > 0) &&
                            <TableWrapper.TableAlignedItems.TableActionButton text={intl.formatMessage({ id: "approve" })} handleClick={handleSelectedHolidaysApprove} />
                        }
                        <TableWrapper.TableAlignedItems.TableActionButton text={intl.formatMessage({ id: "request-holiday" })} icon={<AddIcon />} handleClick={handleOpenDialog} />
                    </TableWrapper.TableAlignedItems.RightAligned>
                </TableWrapper.TableAlignedItems>
                <TableWrapper.TableContent />
            </TableWrapper>

            <HolidayRejectDialog
                openDialog={isRejectDialogOpen}
                employeeInfo={targetHoliday}
                handleCloseDialog={handleCloseRejectDialog}
                handleHolidayReject={handleHolidayRejectSubmit}
                bookedHolidaysCountSelector={getBookedHolidaysCount}
            />

            <ConfirmDeleteDialog
                openDialog={isConfirmDeleteDialogOpen}
                itemToDelete={CONFIRM_DELETE_DIALOG_TITLE}
                handleCloseDialog={handleCloseConfirmDeleteDialog}
                handleDeleteItem={handleConfirmDeleteSubmit}
            />
        </div>
    );
};

const mapDispatchToProps = {
    approveHolidayRequests,
    rejectHolidayRequest,
};

export default connect(null, mapDispatchToProps)(PendingHolidaysTable);
